/* « Démarrer » step 1 · « Votre établissement et sa marque » — the walkable set of record (parcours-marchand-v2,
   guide/1; merchant-app review MA-5…MA-7; FQ-C4). One page: the sector (a model prepares the gifts, the roue and the
   rhythm of the visits), the fiche Google, the brand — with the customer's welcome page beside it.
   « Vos réponses sont gardées si vous quittez » holds: the fiche, the address and « Plus tard » are saved when given, the sector,
   the colour and the logo are kept as a draft until « Suivant » saves them (« Enregistrer et quitter » loses nothing).
   « Suivant » needs a sector, and the fiche, an address typed by hand or an explicit « Plus tard » (FQ-C4; D-49 « never
   a blocking step »; answer (b) 08/10: paste + « Vérifier », search by name is not V1). Without a fiche no platform is
   linked, so nothing asks for a Google review: the builder's default demande is not Google (Stepper), and the guided
   path reaches « en ligne ». The model is applied once, on « Suivant », and only to a blank world (no model yet, no gift
   of the merchant's, no roue: the old Guide's guard): a sector tried and changed on this page creates nothing twice, and a
   world that has its gifts, roues or window keeps them (premier-jour vet). Otherwise « Suivant » saves the sector and the
   brand only, and the page promises no model. */
import { useState } from 'react'
import { Button, Card, Field, Input, useToast } from '@/ui'
import type { Location } from '@/lib/api'
import { getAdresse, getDestinations, getFiche, getFicheLater, readFiche, setAdresse, setFicheLater, TEXT_DEFAULTS } from '@/lib/campaign'
import { SECTEURS, getWindowDays, secteurLabel, setSecteur, useSecteur, type SecteurId } from '@/lib/scope'
import { MODELS, applyModel, modelApplied } from '@/lib/models'
import { getLocalTypes, getRoues, getWheelDesign, setWheelDesign } from '@/lib/wheel'
import { GUIDE_STEPS } from '@/lib/vocab'
import { identityKey } from '@/routes/Accueil'
import ImageDrop from '@/routes/campagnes/ImageDrop'
import WelcomeMini from '@/routes/campagnes/Welcome'
import { currentWorld } from '@/mocks/worlds'
import { apiWrites } from '@/lib/api'
import { editRefusalLines, settled, toastWhenSaved, writeMark } from '@/lib/writes'

/* the page's answers until « Suivant » — saved then, and the draft cleared */
type Draft = { secteur?: SecteurId; colour?: string; logo?: { name: string; url: string } | null }
const draftKey = () => `bmr.guide-etablissement.${currentWorld()}`
function readDraft(): Draft { try { return JSON.parse(localStorage.getItem(draftKey()) ?? '{}') as Draft } catch { return {} } }
function keepDraft(d: Draft) { try { localStorage.setItem(draftKey(), JSON.stringify({ ...readDraft(), ...d })) } catch { /* fine */ } }
function clearDraft() { try { localStorage.removeItem(draftKey()) } catch { /* fine */ } }

const SWATCHES = ['#B89A80', '#237A55', '#2F5D8A', '#B0392E', '#E8804F', '#20261F']
const every = (days: number) => days === 1 ? 'chaque jour' : `tous les ${days} jours`
const tileLine = (id: SecteurId) => MODELS[id].gifts.slice(0, 2).map(g => g.name.charAt(0).toLowerCase() + g.name.slice(1)).join(', ') + '…'

export default function GuideEtablissement({ loc, onNext }: { loc: Location; onNext: () => void }) {
  const toast = useToast()
  const chosen = useSecteur()
  const [draft] = useState(readDraft)
  /* a sector model prepares a blank world only — never one that already has its gifts, its roues or a model */
  const [blank] = useState(() => !modelApplied() && getLocalTypes().length === 0 && getRoues().length === 0)
  const [secteur, setChoix] = useState<SecteurId | null>(draft.secteur ?? chosen)
  /* the fiche — read now (a pasted link), or already a validated Google link of this establishment */
  const [fiche, setFiche] = useState(getFiche())
  /* a fiche the server read nothing from (with an API and no Places key: the link alone) reads as a link, as a validated one */
  const lu = fiche && (fiche.rating || fiche.address || fiche.hours) ? fiche : null
  const googleLinked = getDestinations().some(d => d.locationId === loc.id && d.platform === 'google' && d.state === 'validee')
  const linked = !!fiche || googleLinked
  const [changing, setChanging] = useState(false)
  const [url, setUrl] = useState('')
  const [later, setLater] = useState(getFicheLater())
  /* « Saisir l'adresse à la main »: the name and the address, without a fiche (answer (b) 08/10) */
  const [adresse, setAdr] = useState(getAdresse())
  const [manual, setManual] = useState<{ name: string; address: string } | null>(null)
  /* the brand: the logo and the colour (Marque) — the photo and the sentence are chosen per campaign, at its step 3 */
  const saved = getWheelDesign()
  const [colour, setColour] = useState(draft.colour ?? saved.a)
  /* only a logo the merchant uploaded — no stand-in name on day one (FQ-C4: the product, not the demo) */
  const [logoName, setLogoName] = useState<string | null>(draft.logo !== undefined ? draft.logo?.name ?? null : saved.logo && saved.logoUrl ? 'votre logo' : null)
  const [logoUrl, setLogoUrl] = useState<string | null>(draft.logo !== undefined ? draft.logo?.url ?? null : saved.logoUrl ?? null)
  const pickSecteur = (s: SecteurId) => { setChoix(s); keepDraft({ secteur: s }) }
  const pickColour = (c: string) => { setColour(c); keepDraft({ colour: c }) }
  const pickLogo = (l: { name: string; url: string } | null) => { setLogoName(l?.name ?? null); setLogoUrl(l?.url ?? null); keepDraft({ logo: l }) }
  const design = { ...saved, a: colour, logo: !!logoName, logoUrl: logoName ? logoUrl : null }
  const name = fiche?.name ?? adresse?.name ?? loc.name

  /* 2b-2 · with an API, each answer here waits for the server: the « ✓ » once it is saved, else the refusal's line */
  const [busy, setBusy] = useState(false)
  const saveManual = () => {
    if (!manual || !manual.name.trim() || !manual.address.trim()) return
    const a = { name: manual.name.trim(), address: manual.address.trim() }
    const mark = writeMark()
    setAdresse(a); setAdr(a); setManual(null); setLater(false); setFicheLater(false)
    toastWhenSaved(mark, toast, '✓ Adresse enregistrée.', () => setAdr(getAdresse()))
  }
  const verifier = () => {
    if (!url.trim()) return
    const mark = writeMark()
    const f = readFiche(url.trim(), loc.name)
    setFiche(f); setChanging(false); setUrl(''); setLater(false); setFicheLater(false)
    if (!apiWrites) { toast({ message: `✓ Fiche lue : ${f.name} · ${f.rating} ★. Google est lié.` }); return }
    /* the server reads the fiche when it can (a Places key); without one it keeps the link, and Google is linked all the same */
    setBusy(true)
    void settled(mark).then(no => {
      setBusy(false)
      const read = getFiche(); setFiche(read)
      toast({ message: no ? editRefusalLines(no)[0] : read?.rating ? `✓ Fiche lue : ${read.name} · ${read.rating} ★. Google est lié.` : '✓ Lien enregistré : Google est lié.' })
    })
  }
  const next = () => {
    if (!secteur) { toast({ message: 'Choisissez votre secteur.' }); return }
    if (!linked && !adresse && !later) { toast({ message: 'Liez votre fiche Google, saisissez l’adresse, ou choisissez « Plus tard ».' }); return }
    if (busy) return
    const mark = writeMark()
    setWheelDesign(design); clearDraft()
    try { localStorage.setItem(identityKey(), '1') } catch { /* fine */ }
    let ok: string
    if (blank) {
      const r = applyModel(secteur, { id: loc.id, name: loc.name })
      ok = `✓ ${secteurLabel(secteur)} : ${r.gifts} cadeaux, la roue « ${r.roue.name} » et un quiz sont prêts. Vérifiez et ajustez, page par page.`
    } else {
      /* the sector and the brand only: the gifts, the roues, the quizzes and the window stay as they are */
      setSecteur(secteur)
      ok = '✓ Établissement enregistré.'
    }
    if (!apiWrites) { toast({ message: ok }); onNext(); return }
    /* with an API, step 2 opens once the server has prepared the gifts, the roue and the quiz (the next steps read them) */
    setBusy(true)
    void settled(mark).then(no => { setBusy(false); toast({ message: no ? editRefusalLines(no)[0] : ok }); if (!no) onNext() })
  }

  return (
    <div data-testid="guide-etablissement">
      <p className="t-eyebrow" style={{ margin: '0 0 4px' }}>Démarrer · étape 1 sur 6</p>
      <h2 className="t-page" style={{ margin: '0 0 4px' }}>{GUIDE_STEPS[0].title}</h2>
      <p className="t-body" style={{ margin: '0 0 20px', maxWidth: 680 }}>Où êtes-vous, et à quoi ressemblez-vous&nbsp;? {blank ? 'Un modèle par secteur prépare vos cadeaux, votre roue et le rythme des visites.' : 'Vos cadeaux, vos roues et le rythme des visites ne changent pas.'}</p>
      <div className="cols-2-1">
        <div style={{ display: 'grid', gap: 16, alignContent: 'start' }}>
          <Card>
            <p className="f-label" style={{ margin: '0 0 10px' }}>Votre secteur</p>
            <div className="opts" role="radiogroup" aria-label="Votre secteur" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 200px), 1fr))' }}>
              {SECTEURS.map(s => (
                <label key={s.id} className={'opt' + (secteur === s.id ? ' on' : '')} data-secteur={s.id}>
                  <input type="radio" name="secteur" checked={secteur === s.id} onChange={() => pickSecteur(s.id)} />
                  <span><b>{s.label}</b><br /><span className="f-help">{tileLine(s.id)}</span></span>
                </label>
              ))}
            </div>
            <p className="f-help" style={{ margin: '10px 0 0' }} data-testid="rythme">{!blank
              ? <>Le rythme des visites&nbsp;: une participation par client {every(getWindowDays(loc.id))}. Modifiable dans Mon établissement.</>
              : secteur
                ? <>Le rythme des visites de ce secteur&nbsp;: une participation par client {every(MODELS[secteur].windowDays)}. Modifiable ensuite dans Mon établissement.</>
                : 'Choisissez le plus proche : « Autre » convient à tout commerce.'}</p>
          </Card>

          <Card>
            <p className="f-label" style={{ margin: 0 }}>Votre fiche Google</p>
            <p className="f-help" style={{ margin: '2px 0 12px' }}>Le nom, l’adresse et les horaires viennent de la fiche. Les avis Google y mènent.</p>
            {linked && !changing ? (
              <div className="opt on" style={{ cursor: 'default', alignItems: 'center' }} data-testid="fiche-liee">
                <span style={{ flex: 1 }}><b>{name}{lu ? ` · ${lu.rating} ★` : loc.rating ? ` · ${loc.rating} ★` : ''} · liée</b><br />
                  <span className="f-help">{lu ? `${lu.address} · ${lu.hours}. Lus depuis la fiche, rien à retaper.` : 'Lue depuis la fiche, rien à retaper.'}</span></span>
                <Button size="sm" variant="quiet" onClick={() => setChanging(true)}>Changer de fiche</Button>
              </div>
            ) : (<>
              <Field label="Le lien de votre fiche Google">{id => (
                <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                  <Input id={id} value={url} onChange={e => setUrl(e.target.value)} onKeyDown={e => { if (e.key === 'Enter') verifier() }} placeholder="Collez le lien de votre fiche Google…" style={{ flex: 1, minWidth: 220 }} />
                  <Button onClick={verifier} disabled={!url.trim() || busy}>Vérifier</Button>
                  {changing && <Button variant="quiet" onClick={() => { setChanging(false); setUrl('') }}>Annuler</Button>}
                </div>
              )}</Field>
              {!changing && (manual ? (
                <div style={{ display: 'grid', gap: 10, marginTop: 12 }} data-testid="adresse-main">
                  <Field label="Le nom de l’établissement">{id => <Input id={id} maxLength={80} value={manual.name} onChange={e => setManual({ ...manual, name: e.target.value })} />}</Field>
                  <Field label="L’adresse">{id => <Input id={id} maxLength={200} value={manual.address} onChange={e => setManual({ ...manual, address: e.target.value })} onKeyDown={e => { if (e.key === 'Enter') saveManual() }} placeholder="12 rue de la Paix, 75002 Paris" />}</Field>
                  <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                    <Button onClick={saveManual} disabled={!manual.name.trim() || !manual.address.trim()}>Enregistrer l’adresse</Button>
                    <Button variant="quiet" onClick={() => setManual(null)}>Annuler</Button>
                  </div>
                </div>
              ) : adresse ? (
                <p className="f-help" style={{ margin: '10px 0 0' }} data-testid="adresse-saisie"><b>{adresse.name} · {adresse.address}</b>, saisie à la main. Sans fiche, vos campagnes ne demandent pas d’avis Google. Vous la lierez dans Paramètres › Plateformes. <a href="#" onClick={e => { e.preventDefault(); setManual({ ...adresse }) }}>Modifier</a></p>
              ) : later ? (
                <p className="f-help" style={{ margin: '10px 0 0' }} data-testid="fiche-plus-tard"><b>Plus tard, d’accord.</b> Sans fiche, vos campagnes ne demandent pas d’avis Google. Vous la lierez dans Paramètres › Plateformes. <a href="#" onClick={e => { e.preventDefault(); setLater(false); setFicheLater(false) }}>Annuler</a></p>
              ) : (
                <p className="f-help" style={{ margin: '10px 0 0', display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap' }}>Pas de fiche Google pour l’instant&nbsp;?
                  <Button size="sm" variant="quiet" onClick={() => setManual({ name: loc.name, address: '' })} data-testid="saisir-adresse">Saisir l’adresse à la main</Button>
                  <Button size="sm" variant="quiet" onClick={() => { setLater(true); setFicheLater(true) }} data-testid="plus-tard">Plus tard</Button></p>
              ))}
            </>)}
          </Card>

          <Card>
            <p className="f-label" style={{ margin: '0 0 10px' }}>Votre marque</p>
            <div style={{ display: 'grid', gap: 14 }}>
              <Field label="Logo" help="JPEG, PNG ou WebP, 5 Mo au plus — au centre de la roue et en haut de chaque écran.">{id => (
                <div id={id} style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
                  {logoUrl && logoName && <img src={logoUrl} alt="Votre logo" style={{ width: 44, height: 44, objectFit: 'contain', borderRadius: 8, border: '1px solid var(--bmr-border)', background: 'var(--bmr-surface)' }} />}
                  <ImageDrop label="Déposez votre logo" testid="guide-logo-drop" onUploaded={(u, n) => { pickLogo({ name: n, url: u }); toast({ message: `✓ Logo déposé — ${n}.` }) }}>
                    {logoName ? `${logoName} · remplacer` : 'Déposez votre logo'}
                  </ImageDrop>
                  {logoName && <Button size="sm" variant="quiet" onClick={() => pickLogo(null)}>Retirer le logo</Button>}
                </div>
              )}</Field>
              <Field label="Votre couleur" help="Le texte sur votre couleur passe en blanc ou en noir, selon ce qui se lit le mieux.">{id => (
                <div id={id} role="radiogroup" aria-label="Votre couleur" style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                  {SWATCHES.map(c => (
                    <button key={c} type="button" role="radio" aria-checked={colour.toLowerCase() === c.toLowerCase()} aria-label={`Couleur ${c}`} onClick={() => pickColour(c)}
                      style={{ width: 36, height: 36, borderRadius: '50%', background: c, border: colour.toLowerCase() === c.toLowerCase() ? '3px solid var(--bmr-text)' : '2px solid var(--bmr-border)', boxShadow: colour.toLowerCase() === c.toLowerCase() ? '0 0 0 2px var(--bmr-surface) inset' : 'none', cursor: 'pointer' }} />
                  ))}
                  <label className="f-help" style={{ display: 'inline-flex', gap: 6, alignItems: 'center' }}>une autre
                    <input type="color" value={colour} onChange={e => pickColour(e.target.value)} aria-label="Une autre couleur" style={{ width: 40, height: 32, border: '1px solid var(--bmr-border)', borderRadius: 8, background: 'none' }} />
                  </label>
                </div>
              )}</Field>
              <p className="f-help" style={{ margin: 0 }}>La photo d’accueil et la phrase se choisissent dans chaque campagne, à l’étape « Vérifier ».</p>
            </div>
          </Card>

          <div className="actions">
            <Button variant="primary" onClick={next} disabled={busy} data-testid="guide-suivant">Suivant&nbsp;: vos plateformes →</Button>
            <span className="f-help">Vos réponses sont gardées si vous quittez.</span>
          </div>
        </div>
        <aside aria-label="Ce que voit le client">
          <p className="t-eyebrow">Ce que voit le client</p>
          <div style={{ display: 'grid', justifyItems: 'center', gap: 8 }}>
            <WelcomeMini width={260} layout="couleur" colour={colour} logo={!!logoName} logoUrl={logoName ? logoUrl : null} photo={null}
              sentence={TEXT_DEFAULTS['entry.h'].value.replace('{établissement}', name)} promise={TEXT_DEFAULTS['entry.p'].value} brand={name} design={design} />
            <p className="f-help" style={{ margin: 0, textAlign: 'center' }}>Votre logo et votre couleur, sur chaque écran du client.</p>
          </div>
        </aside>
      </div>
    </div>
  )
}
